import { forwardRef, type HTMLAttributes, type PropsWithChildren } from 'react';
import { type ResourceTypeText } from '@common/types/resource';
import { ResourceImage } from '@components/ui/ResourceImage/ResourceImage';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';

import { type PlantTypeText } from '@/common/types/plant';

import css from './styles.module.scss';

const productItemVariants = cva('relative', {
  variants: {
    variant: {
      light: 'bg-white/10',
      dark: 'bg-black/60',
      default: 'bg-transparent',
    },
    radius: {
      default: 'rounded-lg',
      full: 'rounded-full',
    },
    status: {
      active: css.active,
    },
    overlay: {
      dark: css.darkOverlay,
      red: css.redOverlay,
    },
  },
});

//'outline outline-1 outline-accent'

interface ResourceItemProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren,
    VariantProps<typeof productItemVariants> {
  size: {
    width: string;
    height: string;
  };
  kind: ResourceTypeText | PlantTypeText;
}

const ResourceItem = forwardRef<HTMLDivElement, ResourceItemProps>(
  (
    {
      variant,
      radius,
      status,
      overlay,
      size: { width, height },
      children,
      kind,
      ...props
    },
    ref,
  ) => {
    const size = parseInt(width) < parseInt(height) ? width : height;

    return (
      <div
        style={{
          width,
          height,
        }}
        ref={ref}
        {...props}
        className={cn(
          productItemVariants({ variant, radius, status, overlay }),
          props.className,
        )}
      >
        <ResourceImage
          size={size}
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            overlay === 'red' && 'z-9',
          )}
          kind={kind}
        />
        {children}
      </div>
    );
  },
);

export { ResourceItem };
